import React, { createContext, useContext, useEffect } from "react";
import { useDialog } from "@hooks";
import { InitialValues } from "./reference-form-modal";
import {
  useReference_GetListLazyQuery,
  ReferenceItemFragment,
  useReference_GetConsultantLazyQuery,
  ConsultantReferenceInputNew,
  useReference_AddMutation,
  useReference_EditMutation,
  Reference_getList,
  useReference_SendToRefMutation,
  useReference_ReSendToRefMutation,
  useReference_DeleteMutation,
  useReference_RestoreMutation,
  useReference_ChangeStatusMutation,
  ChangeStatusConsultantReferenceInput,
  useReference_MergeMutation,
  ConsultantQuality_GetConsultantNew
} from "@graphql";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { useReferenceRequestSent } from "./use-reference-request-sent";
import {
  useReferenceUpdateRequestSent
} from "@consultants/consultant-profile-new/references/use-reference-update-request-sent";

type ReferenceStore = {
  consultantId: string;
  references: ReferenceItemFragment[];
  lostReferences: ReferenceItemFragment[];
  cvGranskningApproved: boolean;
  loading?: boolean;
  submitting?: boolean;
  dialog?: ReturnType<
    typeof useDialog<{ reference?: ReferenceItemFragment; refId?: string }>
  >;
  statusDialog?: ReturnType<
    typeof useDialog<{ reference?: ReferenceItemFragment; refId?: string }>
  >;
  mergeDialog?: ReturnType<
    typeof useDialog<{ reference?: ReferenceItemFragment; refId?: string }>
  >;
  areasOfExpertise: string[];
  hasDocument?: boolean;
  onSubmit: (
    input: ConsultantReferenceInputNew,
    onSuccess?: (data: ReferenceItemFragment) => void
  ) => void;
  onDelete: (referenceId: string, softDelete: boolean) => void;
  onRestore: (referenceId: string) => void;
  onSendToRef: (referenceId: string) => void;
  onReSendToRef: (referenceId: string) => void;
  onSendRequest: (data: ReferenceItemFragment) => void;
  onUpdRequest: (data: ReferenceItemFragment) => void;
  onChangeStatus: (
    referenceId: string,
    input: ChangeStatusConsultantReferenceInput,
    file?: File
  ) => void;
  onMergeReference: (referenceId: string, mergeWithRefId: string) => void;
};

type ReferenceStoreParams = any & {
  consultantId: string;
};

const ReferenceStoreContext = createContext<ReferenceStore>(null!);

export const useReferenceStore = () => {
  const context = useContext(ReferenceStoreContext);

  if (context == null) {
    throw new Error("useReferenceStore must be used within ReferenceStoreProvider");
  }

  return context;
};

export const ReferenceStoreProvider = ({
  children,
  consultantId
}: ReferenceStoreParams) => {
  const dialog = useDialog<{ values?: InitialValues; refId?: string }>();
  const statusDialog = useDialog<{ reference?: ReferenceItemFragment }>();
  const mergeDialog = useDialog<{ reference?: ReferenceItemFragment }>();
  const snack = useSnackbarsPipeline();

  const [getList, { data, loading }] = useReference_GetListLazyQuery();
  const [getConsultantData, { data: consultantData }] =
    useReference_GetConsultantLazyQuery();

  const { onRequestSent } = useReferenceRequestSent({
    consultantData: {
      Name: consultantData?.consultantById?.Name,
      _id: consultantId
    },
    refGroupName: "Referens"
  });

  const { onUpdRequestSent } = useReferenceUpdateRequestSent({
    consultantData: {
      Name: consultantData?.consultantById?.Name,
      _id: consultantId
    },
    refGroupName: "Referens"
  });

  const [addReference, { loading: loadingAdd }] = useReference_AddMutation({
    onCompleted: () => {
      snack.showSuccess("Referens sparad");
      dialog.onClose();
    },
    onError: (e) => {
      snack.showError(`Något gick fel: ${e.message}`);
    },
    refetchQueries: [
      Reference_getList.OperationName,
      ConsultantQuality_GetConsultantNew.OperationName
    ]
  });

  const [editReference, { loading: loadingEdit }] = useReference_EditMutation({
    onCompleted: () => {
      snack.showSuccess("Referens uppdaterad");
      dialog.onClose();
    },
    onError: (e) => {
      snack.showError(`Något gick fel: ${e.message}`);
    },
    refetchQueries: [ConsultantQuality_GetConsultantNew.OperationName]
  });

  const [deleteReference] = useReference_DeleteMutation({
    onError: (e) => {
      snack.showError(`Något gick fel: ${e.message}`);
    },
    refetchQueries: [
      Reference_getList.OperationName,
      ConsultantQuality_GetConsultantNew.OperationName
    ]
  });

  const [restoreReference] = useReference_RestoreMutation({
    onCompleted: () => {
      snack.showSuccess("Referens uppdaterad");
      dialog.onClose();
    },
    onError: (e) => {
      snack.showError(`Något gick fel: ${e.message}`);
    },
    refetchQueries: [
      Reference_getList.OperationName,
      ConsultantQuality_GetConsultantNew.OperationName
    ]
  });

  const [sendToRef] = useReference_SendToRefMutation({
    onCompleted: () => {
      snack.showSuccess("Referens skickas");
      dialog.onClose();
    },
    onError: (e) => {
      snack.showError(`Något gick fel: ${e.message}`);
    }
  });


  const [reSendToRef] = useReference_ReSendToRefMutation({
    onCompleted: () => {
      snack.showSuccess("Referens re skickas");
      dialog.onClose();
    },
    onError: (e) => {
      snack.showError(`Något gick fel: ${e.message}`);
    }
  });

  const [changeStatus, { loading: loadingStatus }] = useReference_ChangeStatusMutation({
    onCompleted: () => {
      snack.showSuccess("Referens uppdaterad");
      statusDialog.onClose();
    },
    onError: (e) => {
      snack.showError(`Något gick fel: ${e.message}`);
    },
    refetchQueries: [
      Reference_getList.OperationName,
      ConsultantQuality_GetConsultantNew.OperationName
    ]
  });

  const [mergeReferences, { loading: loadingMerge }] = useReference_MergeMutation({
    onCompleted: () => {
      snack.showSuccess("Referens merged");
      mergeDialog.onClose();
    },
    onError: (e) => {
      snack.showError(`Något gick fel: ${e.message}`);
    },
    refetchQueries: [
      Reference_getList.OperationName,
      ConsultantQuality_GetConsultantNew.OperationName
    ]
  });

  useEffect(() => {
    getList({ variables: { id: consultantId }, fetchPolicy: "cache-and-network" });
    getConsultantData({ variables: { id: consultantId }, fetchPolicy: "cache-first" });
  }, [consultantId]);

  const onSubmit = async (
    input: ConsultantReferenceInputNew,
    onSuccess?: (data: ReferenceItemFragment) => void
  ) => {
    if (dialog.data?.refId) {
      await editReference({
        variables: { input, refId: dialog.data.refId },
        onCompleted: (data) => onSuccess?.(data.updateConsultantReference)
      });
    } else {
      await addReference({
        variables: { input, consultantId },
        onCompleted: (data) => onSuccess?.(data.createConsultantReference)
      });
    }
  };

  const onDelete = (referenceId: string, softDelete: boolean) => {
    deleteReference({
      variables: {
        refId: referenceId,
        softDelete: softDelete
      },
      onCompleted: () => {
        snack.showSuccess(softDelete ? "Referens arkiverad" : "Referens raderad");
        dialog.onClose();
      }
    });
  };

  const onRestore = (referenceId: string) => {
    restoreReference({
      variables: {
        refId: referenceId
      }
    });
  };

  const onSendToRef = (referenceId: string) => {
    sendToRef({
      variables: {
        refId: referenceId
      }
    });
  };

  const onReSendToRef = (referenceId: string) => {
    reSendToRef({
      variables: {
        refId: referenceId
      }
    });
  };

  const onSendRequest = (data: ReferenceItemFragment) => {
    if (data) {
      onRequestSent(data);
    }
  };

  const onUpdRequest = (data: ReferenceItemFragment) => {
    if (data) {
      onUpdRequestSent(data);
    }
  };

  const onChangeStatus = (
    referenceId: string,
    input: ChangeStatusConsultantReferenceInput,
    file?: File,
    onSuccess?: (data: ReferenceItemFragment) => void
  ) => {
    changeStatus({
      variables: {
        refId: referenceId,
        input,
        file
      },
      onCompleted: (data) => onSuccess?.(data.changeStatusConsultantReference)
    });
  };

  const onMergeReference = (
    referenceId: string,
    mergeWithRefId: string,
    onSuccess?: (data: ReferenceItemFragment) => void
  ) => {
    mergeReferences({
      variables: {
        refId: referenceId,
        mergeWithRefId
      },
      onCompleted: (data) => onSuccess?.(data.mergeConsultantReference)
    });
  };

  const references = (data?.consultantReferences || []).filter((ref) => ref.name);
  const lostReferences = (data?.consultantReferences || []).filter(
    (ref) => !ref.name && !ref.deleted
  );

  const swedenQuality = consultantData?.consultantById?.QualityPerCountry?.find(
    (q) => q.Country === "Sweden"
  );
  const cvGranskningApproved =
    swedenQuality?.QualityChecks?.some(
      (check) => check.Name === "CV-granskning" && check.Status?.Name === "Approved"
    ) ?? false;

  return (
    <ReferenceStoreContext.Provider
      value={{
        consultantId,
        references,
        lostReferences,
        cvGranskningApproved,
        loading,
        dialog,
        statusDialog,
        mergeDialog,
        areasOfExpertise: consultantData?.consultantById?.AreasOfExpertise || [],
        onSubmit,
        onDelete,
        onRestore,
        onSendToRef,
        onReSendToRef,
        onSendRequest,
        onUpdRequest,
        onChangeStatus,
        onMergeReference,
        submitting: loadingAdd || loadingEdit || loadingStatus || loadingMerge
      }}
    >
      {children}
    </ReferenceStoreContext.Provider>
  );
};
